import { IHeader } from '@/.generated';
import { NavigationLinkWrapper } from '@/helpers/Wrappers/NavigationLinkWrapper/NavigationLinkWrapper';
import PlainTextWrapper from '@/helpers/Wrappers/PlainTextWrapper/PlainTextWrapper';
import { mobileHeaderVariants } from '../Header.styles';
import { SvgIcon } from '@/helpers/SvgIcon';
import { getCSLPAttributes } from '@/utils/type-guards';
import { visibleNavLinks } from '../visibleNavLinks';
import { trackAdobeClick } from '@/utils/adobe';

// Element types resolved from the IHeader nav tree (deeper levels nest per-link).
// The header renders up to L3 only.
type NavColumn = NonNullable<
  NonNullable<IHeader['nav_level_one']>[number]['nav_columns']
>[number];
type L2Item = NonNullable<NavColumn['nav_level_two']>[number];
type L2Link = NonNullable<L2Item['navigation_level_two_links']>[number];
type L3Item = NonNullable<L2Link['navigation_level_three']>[number];

type MobileNavLevelTwoProps = {
  l2Item: L2Item;
  l2Index: number;
  isVisible: boolean;
  isMegaMenuVisible: boolean;
  onToggle: (index: number) => void;
};

// Render a level-three group nested under a level-two link (deepest level in the header).
const LevelThreeGroup = ({ l3Item }: { l3Item: L3Item }) => {
  const { levelThreeTitle, levelThreeLink } = mobileHeaderVariants();
  const visibleL3Links = visibleNavLinks(l3Item.navigation_level_three_links);
  if (!visibleL3Links.length) return null;

  return (
    <div>
      <PlainTextWrapper
        content={l3Item.navigation_level_three_title}
        cslpAttribute={l3Item.$?.navigation_level_three_title}
        className={levelThreeTitle()}
      />
      <ul>
        {visibleL3Links.map((link, index) => (
          <li key={index}>
            <NavigationLinkWrapper
              navigationLink={link}
              clickLocation="mobileNav"
              siteSection={l3Item.navigation_level_three_title}
              className={levelThreeLink()}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export const MobileNavLevelTwo = ({
  l2Item,
  l2Index,
  isVisible,
  isMegaMenuVisible,
  onToggle,
}: MobileNavLevelTwoProps) => {
  const { levelTwoTitle, levelTwoLink } = mobileHeaderVariants();

  const { subMegaMenu: subMegaMenuClass, chevronIconL2 } = mobileHeaderVariants({
    megaMenuVisible: isMegaMenuVisible,
    subMegaMenuVisible: isVisible,
  });

  const visibleL2Links = visibleNavLinks(l2Item.navigation_level_two_links);

  if (!visibleL2Links.length) return null;

  return (
    <li>
      <button
        className={levelTwoTitle()}
        onClick={(e) => {
          if (!visibleL2Links.length) return;
          e.stopPropagation();
          trackAdobeClick({ clickName: l2Item.nav_level_two_title ?? '' }, e);
          onToggle(l2Index);
        }}
        aria-expanded={isVisible}
        aria-label={l2Item.nav_level_two_title?.trim() || 'Expand navigation section'}
        {...getCSLPAttributes(l2Item.$?.nav_level_two_title)}
      >
        {l2Item.nav_level_two_title}
        <SvgIcon icon="chevron-down" size="s" className={chevronIconL2()} fill="currentColor" />
      </button>

      <div className={subMegaMenuClass()}>
        <ul>
          {visibleL2Links.map((link, index) => (
            <li key={index}>
              <NavigationLinkWrapper
                navigationLink={link}
                clickLocation="mobileNav"
                siteSection={l2Item.nav_level_two_title}
                className={levelTwoLink()}
              />
              {!!link.navigation_level_three?.length &&
                link.navigation_level_three.map((l3Item, l3Index) => (
                  <LevelThreeGroup key={l3Index} l3Item={l3Item} />
                ))}
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
};
